/**
 * my-hiisi-contact.js — T-132d
 *
 * Submit handler for the per-bike contact form (snippets/my-hiisi-contact-
 * form.liquid). Standalone module — no dependency on my-hiisi-hydrate.js or
 * my-hiisi-versions.js, so a load failure in either doesn't disable the
 * contact affordance.
 *
 * Flow:
 *   1. Delegate on click for [data-mht-action="contact-submit"] so forms
 *      rendered per bike-card (and any future dynamically-inserted ones)
 *      all work without per-form wiring.
 *   2. Serialize the closest [data-bmh-contact-form]'s named fields.
 *   3. POST JSON to /apps/hiisi-bespoke/customer/contact (App Proxy base —
 *      same origin, credentials included so the logged_in_customer_id cookie
 *      rides along).
 *   4. Optimistic loading state (disable button + "Sending…"); on success
 *      replace the form body with a confirmation; on error re-enable + show
 *      the failure in the status line. 429 gets a tailored message.
 *
 * No cost/sourcing data touched — the form carries customer-supplied fields
 * + order reference only.
 */
(function () {
  'use strict';

  var ENDPOINT = '/apps/hiisi-bespoke/customer/contact';

  function setStatus(form, text, isError) {
    var status = form.querySelector('[data-bmh-contact-status]');
    if (!status) return;
    status.textContent = text || '';
    status.style.color = isError ? '#d98b8b' : '';
  }

  function serialize(form) {
    var fields = ['order_id', 'line_item_id', 'order_name', 'customer_name', 'customer_email', 'subject', 'message'];
    var body = {};
    for (var i = 0; i < fields.length; i++) {
      var el = form.querySelector('[name="' + fields[i] + '"]');
      body[fields[i]] = el ? String(el.value || '').trim() : '';
    }
    return body;
  }

  function showConfirmation(form, customerEmail) {
    // Replace the form's interactive content with a confirmation message.
    // We keep the <form> element (avoids layout shift) but blank its body.
    var confirm = document.createElement('div');
    confirm.className = 'bmh-contact-form__confirmation';
    confirm.setAttribute('role', 'status');
    var heading = document.createElement('p');
    heading.className = 'bmh-contact-form__confirmation-title';
    heading.textContent = 'Message sent ✓';
    var detail = document.createElement('p');
    detail.className = 'bmh-contact-form__confirmation-detail';
    detail.textContent = customerEmail
      ? 'Pole will reply within 1–2 business days at ' + customerEmail + '.'
      : 'Pole will reply within 1–2 business days.';
    confirm.appendChild(heading);
    confirm.appendChild(detail);
    form.innerHTML = '';
    form.appendChild(confirm);
  }

  function handleSubmit(form, btn) {
    var body = serialize(form);

    // Lightweight client-side guard so empty submits don't round-trip. The
    // Worker validates authoritatively; this is just UX.
    if (!body.subject) { setStatus(form, 'Please add a subject.', true); return; }
    if (!body.message) { setStatus(form, 'Please write a message.', true); return; }

    var originalLabel = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Sending…';
    setStatus(form, '', false);

    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).then(function (res) {
      if (res.ok) {
        showConfirmation(form, body.customer_email);
        return;
      }
      // Re-enable + surface the error. 429 = daily rate-limit.
      btn.disabled = false;
      btn.textContent = originalLabel;
      if (res.status === 429) {
        setStatus(form, "You've reached today's message limit. Please email care@polebicycles.com directly.", true);
      } else {
        setStatus(form, 'Could not send — please try again, or email care@polebicycles.com.', true);
      }
    }).catch(function () {
      btn.disabled = false;
      btn.textContent = originalLabel;
      setStatus(form, 'Network error — please try again, or email care@polebicycles.com.', true);
    });
  }

  function onClick(e) {
    var target = e.target;
    if (!target || target.nodeType !== 1) return;
    var btn = target.closest ? target.closest('[data-mht-action="contact-submit"]') : null;
    if (!btn) return;
    var form = btn.closest ? btn.closest('[data-bmh-contact-form]') : null;
    if (!form) return;
    e.preventDefault();
    handleSubmit(form, btn);
  }

  // Also intercept native form submit (Enter key in a field) so keyboard
  // users get the same JS path instead of a full-page POST to the endpoint.
  function onSubmitCapture(e) {
    var form = e.target;
    if (!form || !form.hasAttribute || !form.hasAttribute('data-bmh-contact-form')) return;
    e.preventDefault();
    var btn = form.querySelector('[data-mht-action="contact-submit"]');
    if (btn && !btn.disabled) handleSubmit(form, btn);
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(function () {
    document.addEventListener('click', onClick);
    document.addEventListener('submit', onSubmitCapture, true);
  });
})();
